import type { ShopifyProduct } from '@/lib/shopify';

const ZIP_TOKENS = ['zip-up', 'zipper', 'zip'];

const stripZip = (handle: string): string | null => {
  for (const token of ZIP_TOKENS) {
    const patterns = [`-${token}-`, `-${token}`, `${token}-`];
    for (const p of patterns) {
      if (handle.includes(p)) {
        return handle.replace(p, p.startsWith('-') && p.endsWith('-') ? '-' : '').replace(/--+/g, '-').replace(/^-|-$/g, '');
      }
    }
  }
  return null;
};

export const hasZipToken = (handle: string): boolean =>
  ZIP_TOKENS.some((t) => new RegExp(`(^|-)${t}(-|$)`).test(handle));

/**
 * Given a product handle and the full product list, return its zip/standard partner.
 * Pairing is purely handle-based: `blessed-hoodie` ↔ `blessed-zip-hoodie`.
 */
export const findHoodiePartner = (
  handle: string,
  allProducts: ShopifyProduct[]
): ShopifyProduct | null => {
  if (!handle) return null;
  const lower = handle.toLowerCase();

  // Current is zip → find standard by stripping the zip token
  if (hasZipToken(lower)) {
    const standardHandle = stripZip(lower);
    if (!standardHandle) return null;
    return allProducts.find((p) => p.node.handle.toLowerCase() === standardHandle) || null;
  }

  // Current is standard → try injecting a zip token after the first segment
  const segments = lower.split('-');
  for (const token of ZIP_TOKENS) {
    const candidates = [
      [segments[0], token, ...segments.slice(1)].join('-'),
      [...segments, token].join('-'),
      [token, ...segments].join('-'),
    ];
    for (const c of candidates) {
      const match = allProducts.find((p) => p.node.handle.toLowerCase() === c);
      if (match) return match;
    }
  }
  return null;
};

export type HoodiePair = {
  standard: ShopifyProduct;
  zip: ShopifyProduct;
};

export const getHoodiePair = (
  product: ShopifyProduct,
  allProducts: ShopifyProduct[]
): HoodiePair | null => {
  const partner = findHoodiePartner(product.node.handle, allProducts);
  if (!partner) return null;
  const currentIsZip = hasZipToken(product.node.handle.toLowerCase());
  return currentIsZip
    ? { standard: partner, zip: product }
    : { standard: product, zip: partner };
};

/**
 * Collapse hoodie pairs in a product list down to a single card.
 * When a product handle contains a zip token AND its standard partner is in the
 * same list, the zip entry is dropped. Order is otherwise preserved.
 */
export const dedupeHoodiePairs = (products: ShopifyProduct[]): ShopifyProduct[] => {
  const handles = new Set(products.map((p) => p.node.handle.toLowerCase()));
  return products.filter((p) => {
    const handle = p.node.handle.toLowerCase();
    if (!hasZipToken(handle)) return true;
    const partner = findHoodiePartner(handle, products);
    if (!partner) return true;
    return !handles.has(partner.node.handle.toLowerCase()) || partner.node.handle.toLowerCase() === handle;
  });
};

